/**
 * The account's CRM feeds — a table, one row each, opening onto the feed itself.
 *
 * ## Why there is a list at all
 *
 * Arun: *"the crm outbound is also per customer account and each customer account can have
 * more than one of these can you design it that way please."* One CRM per account was an
 * assumption baked into a single `?customer=` dropdown; a hospital with a no-show feed, an
 * appointment-confirmation feed and a rebooking feed breaks it. So a feed is a thing an account
 * has several of, and this is where they are enumerated.
 *
 * ## Why it is a table, and why one feed no longer skips it
 *
 * Both were the same mistake. This used to be cards, and it redirected straight through to the
 * feed when an account had exactly one — on the reasoning that clicking through a list of one is
 * a tax on the common case. Arun, 2026-09-03: *"there can be multiple such crm outbond for one
 * accoiunt Eg., no shows, book appointmwnts, appointment confirmation etc. … a table that shows
 * the details before going unto one will help."*
 *
 * He is right, and the redirect was hiding the very page that makes several feeds legible. A
 * table also beats cards for the reason the accounts page is a table: the question here is
 * "which feed do I want, and is any of them quietly broken?", and that is answered by comparing
 * numbers *down a column*, not by reading three cards in turn.
 *
 * ## What a row has to answer without being opened
 *
 * Halted or not, first — a halt is a latch, and a feed that has stopped is the whole reason
 * somebody came here. Then what it *is* (its name and its one-line purpose, both written by
 * whoever configured it), then whether it is even polling, then what it has actually done: rows
 * stored, calls placed, and when it last ran.
 *
 * ## Why there is no pager
 *
 * Every other list in this app grew a pager this week, because they grow with time. This one
 * does not: a feed is a source file in `server/src/orchestrator/customers/`, so the row count
 * changes only when somebody deploys. Ten feeds under one account would be remarkable; a
 * thousand is not a thing that can happen without a thousand pull requests.
 */

import { useEffect, useMemo } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { DateTime } from "luxon";
import type { ColumnDef } from "@tanstack/react-table";
import { FlaskConical, KeyRound, PauseCircle, RefreshCw, ShieldBan, Timer } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { PageHeader } from "@/components/ui/page-header";
import { DataTable } from "@/components/ui/data-table";
import { DenseTableViewport } from "@/components/ui/dense-table-viewport";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorState } from "@/components/ui/error-state";
import { TableSkeleton } from "@/components/table-skeleton";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { errorMessage, type OrchestratorFeed } from "@/lib/api";
import { ADAPTER_LABEL } from "@/lib/crm-labels";
import { relativeTime } from "@/lib/labels";
import { TABLE_ROW_GROUP_CLASS } from "@/lib/table-styles";
import { useOrchestratorForAccount } from "@/lib/queries";

/**
 * The one thing to do about an empty list, in the words of the person who has to do it.
 *
 * Arun, 2026-09-03: *"say that to setup a new CRM based outbound, ask your claude code to do this
 * when pointing from this repo … and give it the apis needed."* The old copy named the folder the
 * file lands in, which is the outcome, not the instruction — nobody reading this screen was going
 * to hand-write a LeadSquared query. What they need is the one sentence that starts the job and
 * the list of what to have ready before they start.
 *
 * Left-aligned and broken into a list on purpose: it started life inside the empty state's
 * `description`, which centres its text, and eight centred lines of instructions is a wall
 * rather than a next step.
 */
function HowToAddAFeed() {
  return (
    <div className="max-w-md space-y-2 text-left text-sm">
      <p>
        <span className="text-foreground font-medium">To add one:</span> open this repo in Claude
        Code and ask it for the feed you want. Have ready:
      </p>
      <ul className="text-muted-foreground list-disc space-y-1 pl-5">
        <li>the CRM's API docs</li>
        <li>a key that can read them</li>
        <li>one real response from the list you want called</li>
      </ul>
      <p className="text-muted-foreground">
        It follows <code>docs/ADD-A-CRM-FEED.md</code> from there. Deploy, type the CRM's keys
        into the new feed's own screen, then dry-run it here before it dials anybody.
      </p>
    </div>
  );
}

export function CrmFeedsPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const feeds = useOrchestratorForAccount(accountId);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "CRM outbound";
  }, []);

  const customers = feeds.data?.customers ?? [];
  const timerOn = feeds.data?.orchestrator_enabled ?? false;
  const columns = useFeedColumns(accountId ?? "", timerOn);

  if (!accountId) return <Navigate to="/accounts" replace />;

  if (feeds.isError) {
    return (
      <AppShell active="crm">
        <div className="p-6">
          <ErrorState
            title="Couldn't load this account's CRM feeds"
            description={errorMessage(feeds.error)}
            onRetry={() => void feeds.refetch()}
          />
        </div>
      </AppShell>
    );
  }

  const elsewhere = (feeds.data?.total_configured ?? 0) - customers.length;

  return (
    <AppShell active="crm">
      <div className="flex h-full w-full max-w-full flex-col">
        <PageHeader
          title="CRM outbound"
          description="Rings the people this account's CRM already says need a call — the no-shows, the unconfirmed appointments, the ones due a rebooking. Each list is one feed, with its own schedule, its own history and its own stop switch."
          actionsSlot={
            <Button
              variant="outline"
              size="sm"
              onClick={() => void feeds.refetch()}
              disabled={feeds.isFetching}
            >
              <RefreshCw className={cn("size-3.5", feeds.isFetching && "animate-spin")} />
              Reload this page
            </Button>
          }
        />

        <div className="flex min-h-0 flex-1 flex-col gap-4 px-6 pb-6">
          {/*
            The switches belong here as well as on the feed. A configured feed that never fires
            because `CRM_ORCHESTRATOR_ENABLED` is unset looks identical to a working one from a
            list, and that is the single most common reason nothing has happened.
          */}
          {feeds.data && !feeds.data.orchestrator_enabled ? (
            <Alert>
              <Timer className="size-4" />
              <AlertDescription>
                <span className="font-medium">The timer is off.</span> Nothing below polls by
                itself — <code>CRM_ORCHESTRATOR_ENABLED</code> is not set on the server, and it is
                read once at startup, so switching it on is a restart. Runs started by hand on a
                feed still work.
              </AlertDescription>
            </Alert>
          ) : null}
          {feeds.data && !feeds.data.can_dial_for_real ? (
            <Alert>
              <FlaskConical className="size-4" />
              <AlertDescription>
                <span className="font-medium">Calls are simulated.</span>{" "}
                <code>DIAL_MODE={feeds.data.dial_mode}</code> — every stage runs and rows are
                stored, but no telephone rings.
              </AlertDescription>
            </Alert>
          ) : null}

          {feeds.isPending ? (
            <TableSkeleton columnCount={6} rowCount={3} />
          ) : customers.length === 0 ? (
            <EmptyState
              title="This account has no CRM feed"
              description={
                elsewhere > 0
                  ? `Nothing here reads a CRM. ${
                      elsewhere === 1
                        ? "One feed is set up on another account"
                        : `${elsewhere} feeds are set up elsewhere`
                    }, and a feed only ever dials through the account it names.`
                  : "No feed is set up on any account yet."
              }
              action={<HowToAddAFeed />}
            />
          ) : (
            <>
              <DenseTableViewport>
                <DataTable
                  columns={columns}
                  data={customers}
                  onRowClick={(row) => navigate(`/accounts/${accountId}/crm/${row.customer}`)}
                  /*
                    The row navigates, so it has to say so. `TableRow` only brings a hover
                    background; without a cursor the <tr> computes `cursor: auto`, which over
                    text is an I-beam — a row that highlights while the cursor says "select this
                    text" is the exact defect Arun reported on the accounts table.
                  */
                  rowClassName={`${TABLE_ROW_GROUP_CLASS} cursor-pointer`}
                  rowHeightClassName="h-14"
                  headerHeightClassName="h-8"
                  /*
                    The default `ellipsis` overflow is what bounds each cell — without it the
                    cell has no width to wrap or clamp against, and a long purpose ran straight
                    out of its column and under the next one. The two-line cells opt back into
                    wrapping locally (`whitespace-normal`), which is how the accounts table does
                    it too.
                  */
                  stickyHeader
                />
              </DenseTableViewport>
              <p className="text-muted-foreground text-xs">
                <span className="text-foreground font-medium">
                  To add a feed, or retune one:
                </span>{" "}
                open this repo in Claude Code and ask it for the feed you want. Give it the CRM's
                API docs, a key that can read them, and one real response from the list you want
                called — it follows <code>docs/ADD-A-CRM-FEED.md</code> from there, and leaves the
                keys themselves to be typed into the new feed's own screen. Then check it with a
                dry run there before it dials anybody. It stays out of the browser on purpose: a
                feed decides who a live customer's agent rings.
              </p>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}

/**
 * Columns.
 *
 * "Feed" is the name and the purpose together, because the name alone is a slug's worth of
 * meaning and the purpose is the sentence that tells two feeds of the same hospital apart.
 * Status comes second for the same reason "Dialling" is second on the accounts table: a halted
 * feed makes every number to its right history rather than news.
 */
function useFeedColumns(accountId: string, timerOn: boolean): ColumnDef<OrchestratorFeed>[] {
  return useMemo(
    () => [
      {
        id: "feed",
        header: "Feed",
        size: 300,
        cell: ({ row }) => {
          const feed = row.original;
          return (
            <div className="flex min-w-0 flex-col justify-center gap-0.5 leading-snug">
              {/*
                A real <Link>, not a <span>: the row click stays a convenience, and the name
                carries what people expect of a name — pointer, hover underline, focus ring,
                keyboard reach, middle-click into a new tab. `stopPropagation` keeps the row
                handler from navigating a second time.
              */}
              <Link
                to={`/accounts/${accountId}/crm/${feed.customer}`}
                onClick={(e) => e.stopPropagation()}
                className="focus-visible:ring-ring/50 truncate rounded-sm font-medium hover:underline focus-visible:ring-[3px] focus-visible:outline-none"
              >
                {feed.label}
              </Link>
              {/*
                Two lines, then an ellipsis. No halt badge here even though a halt is the most
                important thing on the row — the Status column is immediately to the right and
                says it in red with the reason, and saying it twice in adjacent columns is how a
                table stops being scannable.
              */}
              <span
                className="text-muted-foreground line-clamp-2 text-[11px] whitespace-normal"
                title={feed.purpose}
              >
                {feed.purpose}
              </span>
            </div>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        size: 150,
        cell: ({ row }) => <StatusCell feed={row.original} timerOn={timerOn} />,
      },
      {
        id: "schedule",
        header: "Calling hours",
        size: 170,
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col justify-center leading-snug">
            <span className="truncate text-xs">{row.original.window}</span>
            {/*
              "polls every 15 min" was printed for a feed that runs once a day. The interval is
              only the *intraday* cadence; a next-day batch has none, and reads the previous
              day's list once when the window opens.
            */}
            <span className="text-muted-foreground truncate text-[11px]">
              {row.original.polls_intraday
                ? `polls every ${row.original.poll_every_minutes} min`
                : "once a day, at open"}
            </span>
          </div>
        ),
      },
      {
        id: "source",
        header: "Reads / dials",
        size: 130,
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col justify-center leading-snug">
            <span className="truncate text-xs">
              {ADAPTER_LABEL[row.original.source_adapter] ?? row.original.source_adapter}
            </span>
            <span className="text-muted-foreground truncate font-mono text-[11px]">
              agent {row.original.agent_id}
            </span>
          </div>
        ),
      },
      {
        // Stored and called are separate columns rather than one "1,240 / 90" cell: they are
        // meant to be scanned down, and a ratio jammed into one cell has to be read row by row.
        id: "stored",
        header: "Stored",
        size: 80,
        cell: ({ row }) => <Count value={row.original.stats.total} />,
      },
      {
        id: "called",
        header: "Called",
        size: 80,
        cell: ({ row }) => (
          <Count
            value={row.original.stats.by_status.find((s) => s.status === "dialed")?.n ?? 0}
          />
        ),
      },
      {
        id: "last",
        header: "Last run",
        size: 160,
        cell: ({ row }) => <LastRunCell feed={row.original} />,
      },
    ],
    [accountId, timerOn],
  );
}

/**
 * Whether this feed is going to ring anybody, and if not, why not.
 *
 * One state, not a stack: halted wins, then the global timer, then it is simply on. Two of the
 * three are red herrings if the first is true — a halted feed does not care that the timer is
 * running — and three lines of warning in a table cell is noise nobody reads.
 */
function StatusCell({ feed, timerOn }: { feed: OrchestratorFeed; timerOn: boolean }) {
  /*
    A deliberate pause outranks everything, including a halt. Somebody who switched this feed
    off does not need to be told that its credentials are also missing or that the timer is off:
    they know why it is quiet, and the answer they want confirmed is "yes, you turned it off".
    Neutral rather than red — the one state on this page that is nobody's problem.
  */
  if (feed.pause) {
    return (
      <div className="flex min-w-0 flex-col justify-center leading-snug">
        <span className="text-muted-foreground flex items-center gap-1 text-xs">
          <PauseCircle className="size-3 shrink-0" />
          Paused
        </span>
        <span
          className="text-muted-foreground truncate text-[11px]"
          title={feed.pause.note ?? undefined}
        >
          {feed.pause.note ??
            (feed.pause.paused_by_email ? `by ${feed.pause.paused_by_email}` : "by hand")}
        </span>
      </div>
    );
  }
  if (feed.halt) {
    return (
      <div className="flex min-w-0 flex-col justify-center leading-snug">
        <span className="text-destructive flex items-center gap-1 text-xs">
          <ShieldBan className="size-3 shrink-0" />
          Halted
        </span>
        <span
          className="text-muted-foreground truncate text-[11px]"
          title={`${feed.halt.reason}${feed.halt.code ? ` (${feed.halt.code})` : ""}`}
        >
          {feed.halt.reason}
        </span>
      </div>
    );
  }
  /*
    Before "Timer off", and per feed rather than global: a feed with no CRM keys fetches nothing
    whether the timer is on or not, and unlike the timer it is fixable from the feed's own screen.
    A row reading "Polling" with no keys behind it is the state that produces "why has this never
    stored anything".
  */
  if (!feed.credentials_ready) {
    return (
      <div className="flex min-w-0 flex-col justify-center leading-snug">
        <span className="text-warning flex items-center gap-1 text-xs">
          <KeyRound className="size-3 shrink-0" />
          No CRM keys
        </span>
        <span
          className="text-muted-foreground truncate text-[11px]"
          title={feed.credentials_missing.join(", ")}
        >
          {feed.credentials_missing.join(", ").toLowerCase() || "cannot fetch anything"} missing
        </span>
      </div>
    );
  }
  if (!timerOn) {
    return (
      <div className="flex min-w-0 flex-col justify-center leading-snug">
        <span className="text-warning text-xs">Timer off</span>
        <span className="text-muted-foreground text-[11px]">runs only by hand</span>
      </div>
    );
  }
  return <span className="text-success text-xs">Polling</span>;
}

/**
 * When it last did anything, and whether that went well.
 *
 * `started_at` is the run's logical clock and `finished_at` is when it stopped, so this shows the
 * finish where there is one and says "never finished" where there is not — rather than
 * subtracting two clocks that do not belong to each other and calling the answer a duration.
 */
function LastRunCell({ feed }: { feed: OrchestratorFeed }) {
  const last = feed.last_run;
  if (!last) return <span className="text-muted-foreground text-xs">Never run</span>;
  const at = last.finished_at ?? last.started_at;
  const note = last.halted_reason
    ? `halted: ${last.halted_reason}`
    : last.finished_at === null
      ? "never finished"
      : last.dry_run
        ? "dry run"
        : `${last.counts?.dialed ?? 0} called`;
  return (
    <div className="flex min-w-0 flex-col justify-center leading-snug">
      <span className="text-xs">{DateTime.fromISO(at).toFormat("d LLL, HH:mm")}</span>
      <span
        className={cn(
          "truncate text-[11px]",
          last.halted_reason || last.finished_at === null
            ? "text-destructive"
            : "text-muted-foreground",
        )}
        title={note}
      >
        {relativeTime(at)} · {note}
      </span>
    </div>
  );
}

/**
 * A count.
 *
 * Left-aligned under a left-aligned header, tabular so the digits line up down the column. It
 * was right-aligned first, which put the numbers under the *next* column's heading — the one
 * alignment mistake that makes a reader attribute a number to the wrong thing.
 */
function Count({ value }: { value: number }) {
  return (
    <span className={cn("text-xs tabular-nums", value === 0 && "text-muted-foreground")}>
      {value.toLocaleString()}
    </span>
  );
}
